import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./SuggestedProducts.scss";
import { PHONE_CASE_CATEGORY } from "../Products/data/phoneCaseFilters";
import newTag from "../Components/images/assets/new.svg";
import { API_BASE_URL } from "../config";
import { ensureMediaUrl } from "../utils/mediaUrl";

function toAbsoluteUrl(url) {
  return ensureMediaUrl(url) || null;
}

function normalizeProduct(item) {
  const image = toAbsoluteUrl(item?.urls?.[0]);
  if (!image) return null;
  const brand = Array.isArray(item.brand) ? item.brand[0] : item.brand;
  const price = Number(item.price);
  const mrp = Number(item.mrp);
  const discount =
    Number.isFinite(price) && Number.isFinite(mrp) && mrp > price && mrp > 0
      ? Math.round((1 - price / mrp) * 100)
      : 0;

  return {
    id: item._id || item.id,
    title: item.title || "Phone Case",
    brand: brand || "AL MADE",
    price: Number.isFinite(price) ? price : null,
    mrp: Number.isFinite(mrp) && mrp > 0 ? mrp : null,
    discount,
    image,
  };
}

/**
 * PDP suggested-products rail — card language inspired by homepage product cards.
 */
export function SuggestedProducts({
  excludeProductId,
  category = PHONE_CASE_CATEGORY,
  subcategory,
  title = "You may also like",
  limit = 12,
}) {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      const token = localStorage.getItem("token");
      const config = { headers: token ? { Authorization: `Bearer ${token}` } : {} };
      const post = (body) =>
        axios.post(
          `${API_BASE_URL}/products`,
          { ...body, page: 1, size: Math.max(limit + 6, 18) },
          config
        );

      try {
        let mapped = [];

        if (subcategory) {
          const res = await post({
            category: category ? [category] : [],
            subcategory: [subcategory],
            caseType: [],
            brand: [],
            color: [],
            price: [0, 10000],
          });
          mapped = (Array.isArray(res.data) ? res.data : [])
            .map(normalizeProduct)
            .filter(Boolean)
            .filter((p) => String(p.id) !== String(excludeProductId));
        }

        if (mapped.length < 4) {
          const res = await post({
            category: category ? [category] : [],
            subcategory: [],
            caseType: [],
            brand: [],
            color: [],
            price: [0, 10000],
          });
          const fallback = (Array.isArray(res.data) ? res.data : [])
            .map(normalizeProduct)
            .filter(Boolean)
            .filter((p) => String(p.id) !== String(excludeProductId));
          const seen = new Set(mapped.map((p) => String(p.id)));
          for (const item of fallback) {
            if (seen.has(String(item.id))) continue;
            mapped.push(item);
            seen.add(String(item.id));
          }
        }

        if (!cancelled) setProducts(mapped.slice(0, limit));
      } catch (err) {
        console.error("Failed to load suggested products:", err);
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [excludeProductId, category, subcategory, limit]);

  if (!loading && products.length === 0) return null;

  return (
    <section className="suggested-products" aria-label={title}>
      <header className="suggested-products__header">
        <h2 className="suggested-products__title">{title}</h2>
      </header>

      {loading ? (
        <div className="suggested-products__rail" aria-hidden>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="suggested-products__skeleton" />
          ))}
        </div>
      ) : (
        <div className="suggested-products__rail">
          {products.map((product) => (
            <article
              key={product.id}
              className="suggested-card"
              role="link"
              tabIndex={0}
              onClick={() => navigate(`/products/specific/${product.id}`)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(`/products/specific/${product.id}`);
                }
              }}
            >
              <div className="suggested-card__media">
                <img src={product.image} alt="" loading="lazy" />
                <div className="suggested-card__new" aria-hidden>
                  <img src={newTag} alt="" />
                </div>
                <div className="suggested-card__rating">
                  <span>4.2 *</span>
                  <span>| 4.2K</span>
                </div>
              </div>

              <div className="suggested-card__meta">
                <div className="suggested-card__brand">{product.brand}</div>
                <div className="suggested-card__name">{product.title}</div>
                <div className="suggested-card__offer">
                  {product.price != null && <span>Rs. {product.price}</span>}
                  {product.mrp != null && product.mrp > (product.price ?? 0) && (
                    <span>Rs. {product.mrp}</span>
                  )}
                  {product.discount > 0 && <span>({product.discount}% OFF)</span>}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
